/** Student detail collapse. */
import * as React from 'react';
import { User, Mail, Phone, Clock, GraduationCap } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { UserHistoryTimeline } from '@/components/common/details/UserHistoryTimeline';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import {
  STUDENT_DETAIL_TAB_CONFIGS,
  type StudentDetailTab,
  STUDENT_ACTIVE_STATUS_CONFIG,
  STUDENT_PENDING_STATUS_CONFIG,
  STUDENT_SITUATION_CONFIG,
} from '@/components/students/student-configs';
import type { StudentRecord } from '@/types/remote/student.types';
import {
  getAccompanyingTutors,
  getEffectiveContact,
  getStudentCategory,
  getStudentSituation,
  isInternalStudentEmail,
  mapStudentDetailHeaderPrograms,
} from '@/components/students/remote/student-table-utils';
import { StudentFamilyTab } from './student-detail-tabs/StudentFamilyTab';
import { StudentProfileTab } from './student-detail-tabs/StudentProfileTab';
import { StudentTutorsTab } from './student-detail-tabs/StudentTutorsTab';
import { StudentAvailabilityTab } from './student-detail-tabs/StudentAvailabilityTab';
import { StudentWelcomeMeetingTab } from './student-detail-tabs/StudentWelcomeMeetingTab';

/** Props for {@link StudentDetails}. */
interface Props {
  record: StudentRecord;
  onEditSection?: (stepKey: string) => void;
}

/** Active pane on the remote student details panel. */
type StudentTab = StudentDetailTab;

/** Student detail collapse. */
export const StudentDetails: React.FC<Props> = ({ record, onEditSection }) => {
  const [activeTab, setActiveTab] = React.useState<StudentTab>('family');
  const { programs } = useSessionFormData();

  const detailRecord = record;
  const sections = detailRecord.sections ?? {};
  const accompanyingTutors = getAccompanyingTutors(detailRecord);

  const { category, status } = getStudentCategory({
    onboardingStatus: detailRecord.onboardingStatus,
    folderSituation: detailRecord.folderSituation,
    studentSituation: detailRecord.studentSituation,
    user: detailRecord.user,
    linkedUserId: detailRecord.linkedUserId,
  });

  const statusConfig =
    category === 'pre_registered'
      ? STUDENT_PENDING_STATUS_CONFIG[status as keyof typeof STUDENT_PENDING_STATUS_CONFIG]
      : STUDENT_ACTIVE_STATUS_CONFIG[status as keyof typeof STUDENT_ACTIVE_STATUS_CONFIG];

  const effectiveContact = getEffectiveContact(detailRecord);
  const isAdultStudent = !isInternalStudentEmail(detailRecord.user?.email);

  const studentSituation = getStudentSituation({
    onboardingStatus: detailRecord.onboardingStatus,
    studentSituation: detailRecord.studentSituation,
  });
  const situationConfig = STUDENT_SITUATION_CONFIG[studentSituation];
  const SituationIcon = situationConfig?.icon;
  const AccountStatusIcon = statusConfig?.icon ?? Clock;

  const headerPrograms = mapStudentDetailHeaderPrograms(
    (sections['matching_data'] as { programs?: unknown } | undefined)?.programs,
    programs,
  );

  const tabs: TabItem<StudentTab>[] = STUDENT_DETAIL_TAB_CONFIGS.map(
    ({ id, label, icon: Icon }) => ({
      id,
      label,
      icon: <Icon className="w-3 h-3" />,
    }),
  );

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {detailRecord.user?.firstName ??
                  (sections['registration_information'] as any)?.firstName ??
                  '—'}{' '}
                {detailRecord.user?.lastName ??
                  (sections['registration_information'] as any)?.lastName ??
                  '—'}
              </h3>
              {statusConfig && (
                <LabelBadge
                  label={statusConfig.label}
                  icon={AccountStatusIcon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              )}
              {situationConfig && (
                <LabelBadge
                  label={situationConfig.label}
                  icon={SituationIcon}
                  variant={situationConfig.badgeVariant}
                  color={situationConfig.badgeColor}
                  size="sm"
                />
              )}
              {headerPrograms.map(({ id, label, cfg }) => (
                <LabelBadge
                  key={id}
                  label={label}
                  icon={cfg?.icon ?? GraduationCap}
                  variant={cfg?.badgeVariant ?? 'outlined'}
                  color={cfg?.badgeColor ?? 'var(--color-1-nuance-2)'}
                  size="sm"
                />
              ))}
            </div>
            <div className="details-view-header-subtitle flex items-center gap-2 flex-wrap">
              {effectiveContact.email && (
                <LabelBadge
                  label={effectiveContact.email}
                  icon={Mail}
                  variant="outlined"
                  color="rgb(var(--color-1-nuance-1))"
                  size="sm"
                />
              )}
              {effectiveContact.phone && (
                <LabelBadge
                  label={effectiveContact.phone}
                  icon={Phone}
                  variant="outlined"
                  color="rgb(var(--color-1-nuance-1))"
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs items={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id)} />

      {activeTab === 'family' && (
        <StudentFamilyTab
          detailRecord={detailRecord}
          isAdultStudent={isAdultStudent}
          statusConfig={statusConfig}
          situationConfig={situationConfig}
        />
      )}

      {activeTab === 'profile' && (
        <StudentProfileTab detailRecord={detailRecord} sections={sections} />
      )}

      {activeTab === 'tutors' && <StudentTutorsTab accompanyingTutors={accompanyingTutors} />}

      {activeTab === 'availability' && (
        <StudentAvailabilityTab sections={sections} programs={programs} />
      )}

      {activeTab === 'welcome_meeting' && (
        <StudentWelcomeMeetingTab
          userId={detailRecord.userId}
          sections={sections}
          onEditSection={onEditSection}
        />
      )}

      {activeTab === 'history' && (
        <UserHistoryTimeline userId={detailRecord.userId} role="student_remote" />
      )}
    </>
  );
};

/** Factory for the {@link DataTable} expanded-row collapse. */
export const renderStudentDetails = (
  record: StudentRecord,
  onEditSection?: (stepKey: string) => void,
): React.ReactNode => <StudentDetails record={record} onEditSection={onEditSection} />;
